import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, FlowGrid, BillboardText } from '@fs/zion-ui'
import { getUser } from '@fs/zion-user'
import { useSlsStateContext } from '@fs/zion-sls'
import { FullTextApp } from '../../../../shared/services/config'
import FamilyMembersCardEditMode from './FamilyMembersCardEditMode'
import FamilyMembersReviewList from './components/FamilyMembersReviewList'
import FamilyMembersHeader from './components/FamilyMembersHeader'
import { DISABLED_TEXT_COLOR } from '../shared/utils/panelConstants'
import { getPersonsAndRelationshipsForHousehold } from './utils/familyMember.utils'
import useRecords from '../../../views/indexing/hooks/useRecords'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

/**
 * FamilyMembersCard
 *
 * Displays a list of family member relationships for a given sls person object and
 * provides an inline edit mode for adding / modifying those relationships.
 *
 * Render Modes:
 *  - Review (default): Shows header and (if any) relationship review list.
 *  - Edit: When local edit mode is toggled or `editStep` prop is true, renders the edit form
 *          (`FamilyMembersCardEditMode`).
 *
 * Data Flow:
 *  - Relationships are derived from SLS state via `getPersonRelationships` for the supplied `person`.
 *  - Saving in edit mode is delegated to `FamilyMembersCardEditMode`, which calls `onSave` when complete.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.person               SLS person object used to look up relationships (must contain at least id / recordId).
 * @param {Function} [props.onSave]             Callback fired after a successful save (receives form data from edit mode).
 * @param {boolean}  [props.reviewed=false]     Whether the card is in pending mode (not reviewed) or reviewed.
 * @param {Function} [props.setReviewed]        Setter to update reviewed state (used in edit mode).
 * @param {boolean}  [props.editMode=false]     Forces the card into edit mode (wizard / step-based flows).
 * @param {Function} [props.setEditMode]        Setter to update edit mode state.
 * @param {boolean}  [props.isInReviewProcess=false] Indicates if the parent panel is in review process mode for newly added people.
 * @param {boolean}  [props.saveCardDataToSls=false]        Whether to save the card data to SLS upon submission.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 * @param {string}   [props.appName=FullTextApp] The application name for SLS operations.
 *
 *
 * @returns {JSX.Element}
 */
export const FamilyMembersCard = ({
  person,
  onSave,
  reviewed = false,
  setReviewed,
  editMode = false,
  setEditMode,
  isInReviewProcess = false,
  onDirtyChange = () => {},
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  appName = FullTextApp,
}) => {
  const { images, currentImageArk } = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [userId, setUserId] = useState(null)

  const { allRecords } = useRecords({
    images,
    currentImageArk,
    thumbnails,
  })

  // Determine the recordId to use: prefer person.recordId, else look for a superElement starting with '1:2'
  const recordId = person?.recordId || person?.superElements?.find((se) => se?.id?.startsWith('1:2'))?.id || null

  const { relationships, record } = getPersonsAndRelationshipsForHousehold({
    person,
    images,
    allRecords,
    recordId,
    currentPartitionKey: currentImageArk,
  })

  useEffect(() => {
    getUser().then((u) => setUserId(u.cisId))
  }, [])

  if (!onSave) {
    console.error('ReviewCard: onSave prop is required but was not provided')
    return null
  }

  return (
    <Card margins="xxs" edges={editMode ? 'elevation' : 'border'}>
      <BillboardText primaryColor={!reviewed && !editMode ? DISABLED_TEXT_COLOR : undefined}>
        <FlowGrid orientation="vertical" columnCount={1} gutters={editMode ? 'xs' : 'xxs'}>
          <FamilyMembersHeader setIsEditing={setEditMode} isEditing={editMode} reviewed={reviewed} />
          {!editMode && (
            <FamilyMembersReviewList
              record={record}
              person={person}
              relationships={relationships}
              reviewed={reviewed}
              isInReviewProcess={isInReviewProcess}
            />
          )}
          {editMode && (
            <FamilyMembersCardEditMode
              person={person}
              allRecords={allRecords}
              relationships={relationships}
              reviewed={reviewed}
              setReviewed={setReviewed}
              setIsEditing={setEditMode}
              userId={userId}
              onSave={onSave}
              onDirtyChange={onDirtyChange}
              saveCardDataToSls={saveCardDataToSls}
              splitScreenPartitionKey={splitScreenPartitionKey}
              appName={appName}
            />
          )}
        </FlowGrid>
      </BillboardText>
    </Card>
  )
}

FamilyMembersCard.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string.isRequired,
    // recordId can be null for floating persons (persons not yet attached to a record)
    recordId: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  reviewed: PropTypes.bool,
  setReviewed: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  isInReviewProcess: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
  appName: PropTypes.string,
}

FamilyMembersCard.defaultProps = {
  reviewed: false,
  editMode: false,
  setEditMode: undefined,
}
